import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { FileText, Loader2, Download, AlertTriangle, Calendar } from "lucide-react";

export default function MyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/reports").then(({ data }) => {
      setReports(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const download = async (id) => {
    const res = await fetch(`${API_BASE}/reports/${id}/pdf`, {
      credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 md:max-w-4xl md:mx-auto" data-testid="my-reports-page">
      <div>
        <div className="label-eyebrow">History</div>
        <h1 className="mt-1 text-2xl font-heading font-semibold">My reports</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : reports.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <FileText className="h-10 w-10 mx-auto text-clinical-subtext" />
          <h3 className="mt-3 font-heading text-lg">No reports yet</h3>
          <p className="mt-1 text-sm text-clinical-subtext">
            Submit your first shift report from the Clients tab.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {reports.map((r) => (
            <li key={r.id} className="card-surface p-4">
              <div
                onClick={() => setOpen(open === r.id ? null : r.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(open === r.id ? null : r.id); }}
                role="button"
                tabIndex={0}
                className="w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-clinical-primary/30 rounded-lg"
                data-testid={`report-row-${r.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-base font-medium truncate">
                        {r.client_name}
                      </h3>
                      {r.incident_flag && (
                        <span className="pill bg-destructive/10 text-destructive">
                          <AlertTriangle className="h-3 w-3" /> Incident
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-clinical-subtext">
                      <Calendar className="h-3 w-3" />
                      {r.shift_date} · <span className="capitalize">{r.shift_type}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); download(r.id); }}
                    className="btn-ghost"
                    data-testid={`download-pdf-${r.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {open === r.id && (
                <div className="mt-3 border-t border-clinical-border pt-3 text-sm whitespace-pre-wrap leading-relaxed">
                  {r.formatted_notes || r.raw_notes}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
